'use client';

import { ESessionType, SocketSessionManager } from '@mindverse/accessor-open';
import cookie from 'react-cookies';
import { create } from 'zustand';

import { EGlobalCookieKey } from '@/types/cookie';
import type { TNotice } from '@/types/wsNotice';
import { EVENT } from '@/utils/event';
import { GetFingerPrint } from '@/utils/fingerprint';
import { gatewayHost, isTrulyProd } from '@/utils/host';
import getTimezone from '@/utils/timezone';

interface IChatStore {
  sessionSocketManager: SocketSessionManager | undefined;
  isInitializing: boolean;
  lastInitOptions: { userId?: string; allowAnonymous: boolean } | null;
  initSocketManager: (options: { userId?: string; allowAnonymous?: boolean }) => Promise<void>;
  destroySocketManager: () => void;
}

const processedMessageIds = new Set<string>();

const useChatStore = create<IChatStore>((set, get) => ({
  sessionSocketManager: undefined,
  isInitializing: false,
  lastInitOptions: null,

  initSocketManager: async (options) => {
    const { userId, allowAnonymous = false } = options;

    console.log('[WS-DEBUG] initSocketManager called', { userId, allowAnonymous });

    // 如果不允许匿名且没有 userId，则不初始化
    if (!allowAnonymous && !userId) {
      console.log('[WS-DEBUG] skip: no userId and not allowAnonymous');

      return;
    }

    // 检查参数是否与上次相同
    const lastOptions = get().lastInitOptions;
    const isSameOptions =
      lastOptions && lastOptions.userId === userId && lastOptions.allowAnonymous === allowAnonymous;

    // 如果正在初始化
    if (get().isInitializing) {
      // 参数相同则直接返回
      if (isSameOptions) {
        console.log('[WS-DEBUG] skip: isInitializing and sameOptions');

        return;
      }

      // 参数不同,先销毁当前的,再重新初始化
      console.log('[WS-DEBUG] isInitializing but different options, destroying...');
      get().destroySocketManager();
    }

    // 如果参数相同且已经初始化过,则跳过
    if (isSameOptions && get().sessionSocketManager) {
      console.log('[WS-DEBUG] skip: sameOptions and manager exists');

      return;
    }

    set({ isInitializing: true });

    try {
      const existingManager = get().sessionSocketManager;

      const fp = new GetFingerPrint();

      // 等待 fp 获取完成,最多等待 3 秒
      const startTime = Date.now();
      const fpValue = await fp.getValueAsync(3000);
      const waitTime = Date.now() - startTime;

      console.log('[WS-DEBUG] fp result', {
        fpValue,
        waitTime,
        hasExistingManager: !!existingManager
      });

      const timezone = getTimezone();
      const token = cookie.load(EGlobalCookieKey.token);

      const headers = {
        ...(timezone ? { timeZone: timezone } : {}),
        ...(token ? { token } : {}),
        ...(fpValue ? { fp: fpValue } : {})
      };

      if (isTrulyProd() && !(window as any).inApp && userId && token) {
        // @ts-expect-error any
        headers['encipher'] = 'true';
      }

      // 如果已存在 manager，使用 updateConfig 更新配置
      if (existingManager) {
        existingManager.updateConfig({
          baseURL: gatewayHost(),
          requestHeader: headers,
          userId,
          APIVersion: '1.3.0',
          sessionConnectConfig: {
            deviceId: 'deviceId'
          },
          noticeConfig: {
            onNoticeMessage(msg) {
              const data = msg?.data as TNotice | undefined;

              if (data && !processedMessageIds.has(msg.messageId)) {
                processedMessageIds.add(msg.messageId);
                dispatchEvent(new CustomEvent(EVENT.WS_NOTICE, { detail: data }));
              }
            }
          },
          wsEventListenerConfig: {
            onMessage(message: any) {
              console.log('123123123 WebSocket message:', message);

              let sessionType = message?.sessionType;
              const templateStr = message?.multipleData?.[0]?.modal?.template;

              if (templateStr?.includes('CircleMemberJoin')) {
                sessionType = ESessionType.GROUP;
              }

              if (
                (sessionType == ESessionType.FRIEND || sessionType == ESessionType.GROUP) &&
                message?.index != -1
              ) {
                dispatchEvent(
                  new CustomEvent(EVENT.UPDATE_FRIEND_SESSION, {
                    detail: {
                      sessionId: message?.sessionId,
                      userId: message?.sendUserId,
                      sessionType: sessionType
                    }
                  })
                );
              }
            }
          }
        });

        set({
          lastInitOptions: { userId, allowAnonymous }
        });
      } else {
        // 首次创建 manager
        const socketManager = new SocketSessionManager({
          config: {
            baseURL: gatewayHost(),
            requestHeader: headers,
            userId,
            APIVersion: '1.3.0',
            sessionConnectConfig: {
              deviceId: 'deviceId'
            },
            noticeConfig: {
              onNoticeMessage(msg) {
                const data = msg?.data as TNotice | undefined;

                if (data && !processedMessageIds.has(msg.messageId)) {
                  processedMessageIds.add(msg.messageId);
                  dispatchEvent(new CustomEvent(EVENT.WS_NOTICE, { detail: data }));
                }
              }
            },
            wsEventListenerConfig: {
              onMessage(message: any) {
                console.log('123123123 WebSocket message:', message);

                let sessionType = message?.sessionType;
                const templateStr = message?.multipleData?.[0]?.modal?.template;

                if (templateStr?.includes('CircleMemberJoin')) {
                  sessionType = ESessionType.GROUP;
                }

                if (
                  (sessionType == ESessionType.FRIEND || sessionType == ESessionType.GROUP) &&
                  message?.index != -1
                ) {
                  dispatchEvent(
                    new CustomEvent(EVENT.UPDATE_FRIEND_SESSION, {
                      detail: {
                        sessionId: message?.sessionId,
                        userId: message?.sendUserId,
                        sessionType: sessionType
                      }
                    })
                  );
                }
              }
            }
          },
          showDebug: true
        });

        set({
          sessionSocketManager: socketManager,
          lastInitOptions: { userId, allowAnonymous }
        });
      }
    } finally {
      set({ isInitializing: false });
    }
  },

  destroySocketManager: () => {
    const manager = get().sessionSocketManager;

    if (manager) {
      manager.destructor();
      set({ sessionSocketManager: undefined, lastInitOptions: null });
    }
  }
}));

export default useChatStore;
